import React, { useEffect, useState } from "react";
import Sidebar from "@/components/Sidebar";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { List } from "@phosphor-icons/react";

/**
 * Formats a Date as `DDD MMM DD · HH:MM:SS` in 24-hour time.
 * BSVAC runs on strict military time everywhere in the console.
 */
function formatClock(d) {
  const weekday = d.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase();
  const month = d.toLocaleDateString(undefined, { month: "short" }).toUpperCase();
  const day = d.getDate();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${weekday} ${month} ${day} · ${hh}:${mm}:${ss}`;
}

export default function AppLayout({ children, title, subtitle, actions }) {
  const { user } = useAuth();
  // Ticking clock — updates every second so the topbar time is always live,
  // regardless of route changes or component re-renders. Cleanup on unmount
  // prevents runaway timers.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const timeStr = formatClock(now);
  // Off-canvas sidebar state for phones — always closed on md+ where the
  // sidebar is a permanent rail. Kept in this parent so both the
  // hamburger button and the sidebar overlay stay in sync.
  const [navOpen, setNavOpen] = useState(false);

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar mobileOpen={navOpen} onMobileClose={() => setNavOpen(false)} />
      <main className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="bg-white border-b border-slate-200 px-3 sm:px-6 py-3 flex items-center justify-between gap-3" data-testid="topbar">
          <div className="flex items-center gap-2 min-w-[160px] flex-1">
            {/* Hamburger — mobile only. Toggles the off-canvas sidebar. */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden shrink-0"
              onClick={() => setNavOpen((v) => !v)}
              aria-label="Toggle navigation"
              data-testid="mobile-nav-toggle"
            >
              <List size={22} weight="bold" />
            </Button>
            <div className="min-w-0">
              <div className="tab-tag text-slate-500 truncate">BSVAC / CBVAC // {user?.role?.toUpperCase()}</div>
              <h1 className="font-display text-lg sm:text-2xl font-black tracking-tight text-slate-900 truncate">{title}</h1>
              {subtitle && <p className="text-xs text-slate-500 mt-0.5 truncate hidden sm:block">{subtitle}</p>}
            </div>
          </div>
          <div className="flex items-center gap-4 min-w-0 shrink">
            {actions}
            <div className="hidden md:block text-right">
              <div className="tab-tag text-slate-500">Local Time (24h)</div>
              <div className="mono text-sm text-slate-900" data-testid="local-time">{timeStr}</div>
            </div>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto p-4 md:p-6">{children}</div>
      </main>
    </div>
  );
}
